/* eslint-disable react-hooks/exhaustive-deps */
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FilterOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { Button, notification, Spin, Tooltip } from 'antd'
import dayjs from 'dayjs'
import { Key, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { useLocation } from 'react-router-dom'

import { QUERY_KEYS } from '@api'
import { DateFromToFilter, DeleteTicketModal, DeliveryNote, TIME } from '@components'
import { useFilters, useGeneral, useHandleDeleteTicketModal } from '@contexts'
import {
  useCarriers,
  useCreateTicket,
  useDeleteTicket,
  useEquipment,
  useGetTickets,
  useMaterials,
  useTickets,
} from '@hooks'
import { CommonTicketForm, Filters, PATHS, StatusTicketEnum, Ticket, TripTypeEnum } from '@types'

import { useSocket } from '../../../hooks/useSocket'
import CollapseExpand from '../../../components/CollapseExpand'
import ExpandableTable from '../../../components/ExpandableTable'
import { MapsModal } from '../../../components/MapsModal/MapsModal'
import RangeTimeSwitcher from '../../../components/RangeTimeSwitcher'
import {
  ActionsCellContainer,
  ContentContainer,
  StyledButton,
  StyledRow,
  StyledSelect,
} from '../../../components/StyledComponents'
// import { TicketModal } from '../../../components/TicketModal'
import { forbiddenSupplierEditionStatus } from '../../../constants'
import { useHandleTicketModal } from '../../../contexts/HandleTicketModalContext'
import prepareDataToExpandableTable from '../../../utils/prepare-data-to-expandable-table'
import { AddTicketModalSupplier } from '../AddTicketModalSupplier'
import { UpdateTicketModalSupplier } from '../UpdateTicketModalSupplier'

type ModalAction = 'update' | 'add' | 'detail'
const createdTicketStates: ModalAction[] = ['detail', 'update']
const SuppliersDashboard = () => {
  const { CREATED, TRANSIT, NO_SHOW } = StatusTicketEnum
  const { t } = useTranslation()
  const { createTicket, isLoading: isCreateTicketLoading } = useCreateTicket()
  const { isLoading: isDeleteTicketLoading } = useDeleteTicket()
  const { ticketToEdit, setTicketToEdit, setIsTicketModalOpen } = useHandleTicketModal()
  const { isDeleteTicketModalOpen, setIsDeleteTicketModalOpen } = useHandleDeleteTicketModal()
  const [ticketToDelete, setTicketToDelete] = useState(0)
  const { filters, setFilters, resetFilters } = useFilters()
  const { changeTicket, changeTicketLoading } = useTickets()
  const { tickets, ticketsIsLoading } = useGetTickets(filters)

  // TODO: Add WebSocket functionality for real-time ticket updates
  // This will be implemented once socket.io-client is properly installed

  const { equipmentOptions } = useEquipment()
  const { materialsOptions } = useMaterials()
  const { pathname } = useLocation()
  const {
    ticketToSearch,
    setTicketToSearch,
    filtersDisplayed,
    handleDisplayFilters,
    menuDisplayed,
  } = useGeneral()
  const { allCarriersOptions, isLoadingAllCarriers } = useCarriers()
  const queryClient = useQueryClient()
  const [isMapsModalOpen, setIsMapsModalOpen] = useState(false)
  const [locationLatitude, setLocationLatitude] = useState<number | undefined>(undefined)
  const [locationLongitude, setLocationLongitude] = useState<number | undefined>(undefined)
  const [locationStreet, setLocationStreet] = useState<string[] | string | undefined>(undefined)
  const [locationCity, setLocationCity] = useState<string[] | string | undefined>(undefined)
  const [locationState, setLocationState] = useState<string[] | string | undefined>(undefined)
  const [locationNumber, setLocationNumber] = useState<string[] | string | undefined>(undefined)
  const [tag, setTag] = useState<string | undefined>(undefined)
  const [expandedRows, setExpandedRows] = useState<readonly Key[]>([])
  const [openModal, setOpenModal] = useState<ModalAction>()

  // Conexión WebSocket y actualizaciones de tickets
  const { connect, socket } = useSocket()

  // Conectar a WebSocket cuando el componente se monta
  useEffect(() => {
    connect()
  }, [connect])

  // Escuchar actualizaciones de tickets directamente en el socket
  useEffect(() => {
    if (!socket) return

    const handleTicketUpdate = () => {
      queryClient.invalidateQueries([QUERY_KEYS.TICKETS])
      queryClient.invalidateQueries([QUERY_KEYS.TICKET])
      queryClient.invalidateQueries([QUERY_KEYS.TO_AUTHORIZE])
    }

    socket.on('ticket-update', handleTicketUpdate)

    return () => {
      socket.off('ticket-update', handleTicketUpdate)
    }
  }, [socket, queryClient])

  const preparedData = useMemo(
    () => prepareDataToExpandableTable(tickets?.tickets) ?? [],
    [tickets],
  )
  const handleSetRowData: (expandedKeys?: readonly Key[]) => void = expandedKeys => {
    setExpandedRows(expandedKeys ?? [])
  }

  const handleSelectTicket = (ticket: Ticket) => {
    setTicketToEdit(ticket.id)
    setIsTicketModalOpen(true)
    setOpenModal('detail')
  }

  const handleCloseTicketModal = () => {
    setIsTicketModalOpen(false)
    setTicketToEdit(0)
    setOpenModal(undefined)
  }

  useEffect(() => {
    if (ticketToSearch && tickets?.total === 1) {
      const [ticket] = tickets?.tickets
      if (ticket) {
        setFilters((prevState: Filters) => ({
          ...prevState,
          dateFrom: dayjs(tickets?.tickets[0].withdrawalDate).startOf('d').toDate(),
          dateTo: dayjs(tickets?.tickets[0].withdrawalDate).endOf('d').toDate(),
        }))
      }
      handleSetRowData([tickets.tickets[0].withdrawalDay!])
    }
  }, [tickets])

  const orderByOptions = [
    {
      label: `${t('suppliers_router.filter_options.order_by.ascending')}`,
      value: 'ASC',
    },
    {
      label: `${t('suppliers_router.filter_options.order_by.descending')}`,
      value: 'DESC',
    },
  ]

  const submitHandler = (
    id: number | undefined,
    form: CommonTicketForm,
    resetForm: () => void,
    dates: Date[] | undefined,
  ) => {
    if (id) {
      changeTicket(
        {
          id,
          payload: {
            ...form,
            ticketDate: dates![0],
          },
        },
        {
          onSuccess: () => {
            notification.success({
              message: `Ticket ${id} modificado con éxito.`,
            })
            resetForm()
          },
        },
      )
    } else {
      createTicket(
        {
          ...form,
          ticketsDate: dates,
        },
        {
          onSuccess: () => {
            resetForm()
          },
        },
      )
    }
  }

  const filterOptions = [
    {
      label: `${t('admin_router.filter_options.status.created')}`,
      value: StatusTicketEnum.CREATED,
    },
    {
      label: `${t('admin_router.filter_options.status.pending')}`,
      value: StatusTicketEnum.PENDING,
    },
    {
      label: `${t('admin_router.filter_options.status.confirmed')}`,
      value: StatusTicketEnum.CONFIRMED,
    },
    {
      label: `${t('admin_router.filter_options.status.supplier_invoiced')}`,
      value: StatusTicketEnum.TRANSIT,
    },
    {
      label: `${t('admin_router.filter_options.status.delivered')}`,
      value: StatusTicketEnum.DELIVERED,
    },
    {
      label: `${t('globals.generics.status.no_show')}`,
      value: StatusTicketEnum.NO_SHOW,
    },
  ]

  const handleStatusFilterChange = (value: any) => {
    setFilters((prevState: Filters) => ({
      ...prevState,
      page: 1,
      statusName: [...(value as string[])],
      ticketId: undefined,
    }))
  }

  const handleMaterialFilterChange = (value: any) => {
    setFilters((prevState: Filters) => ({
      ...prevState,
      page: 1,
      materialId: [...(value as number[])],
      ticketId: undefined,
      invoiceNumber: undefined,
      taInvoiceNumber: undefined,
    }))
  }

  const handleOrderByFilterChange = (value: any) => {
    setFilters((prevState: Filters) => ({
      ...prevState,
      page: 1,
      orderBy: value as string,
      ticketId: undefined,
      invoiceNumber: undefined,
      taInvoiceNumber: undefined,
    }))
  }

  const handleEquipmentFilterChange = (value: any) => {
    setFilters((prevState: Filters) => ({
      ...prevState,
      page: 1,
      equipmentId: [...(value as number[])],
      ticketId: undefined,
      invoiceNumber: undefined,
      taInvoiceNumber: undefined,
    }))
  }

  const handleCarrierFilterChange = (value: any) => {
    setFilters((prevState: Filters) => ({
      ...prevState,
      page: 1,
      carrierId: [...(value as number[])],
      ticketId: undefined,
      invoiceNumber: undefined,
      taInvoiceNumber: undefined,
    }))
  }

  const handleCleanFilters = () => {
    setTicketToSearch(undefined)
    handleSetRowData()
    setFilters((prevState: Filters) => ({
      ...prevState,
      page: 1,
      carrierId: [],
      carrierMode: [],
      statusName: [],
      supplierId: [],
      equipmentId: [],
      materialId: [],
      orderBy: undefined,
      ticketId: undefined,
      invoiceNumber: undefined,
      taInvoiceNumber: undefined,
    }))
    resetFilters()
    queryClient.resetQueries(QUERY_KEYS.TICKETS)
  }

  const handleDeleteTicket = (ticket: Ticket) => {
    const withdrawalDate = dayjs(ticket.withdrawalDate).unix()
    const today = dayjs().unix()
    if (withdrawalDate > today && ticket.id !== 0) {
      setIsDeleteTicketModalOpen(true)
      setTicketToDelete(ticket.id)
    }
  }

  const actionsBody = (ticket: Ticket) => {
    const withdrawalDate = dayjs(ticket.withdrawalDate)
    const twentyFourHsFromNow = dayjs().add(1, 'd')
    return (
      <ActionsCellContainer>
        {(ticket.status.name === CREATED && withdrawalDate > twentyFourHsFromNow) ||
        (ticket.tripType === TripTypeEnum.CFR &&
          !forbiddenSupplierEditionStatus.includes(ticket?.status.name as StatusTicketEnum)) ? (
          <EditOutlined
            onClick={() => {
              handleOpenEditTicketModal(ticket)
            }}
          />
        ) : (
          <EyeOutlined
            onClick={() => {
              handleSelectTicket(ticket)
            }}
          />
        )}
        {withdrawalDate > dayjs() &&
          ticket.status.name !== TRANSIT &&
          ticket.status.name !== NO_SHOW && (
            <DeleteOutlined onClick={() => handleDeleteTicket(ticket)} />
          )}
      </ActionsCellContainer>
    )
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFilterOptions = (input: string, option: any) => {
    if (option.label) return option.label.toString().toLowerCase().includes(input.toLowerCase())
    return true
  }

  const handleCleanSearchInput = () => {
    setTicketToSearch(undefined)
  }

  const deliveryNoteTemplate = (ticket: Ticket) => <DeliveryNote ticket={ticket} />

  const handleCloseMapsModal = () => {
    setIsMapsModalOpen(false)
  }

  const handleOpenMapsModal = (ticket: Ticket, title: string) => {
    setIsMapsModalOpen(true)
    if (title === 'origen') {
      setLocationLatitude(ticket.origin.location.coordinates[0])
      setLocationLongitude(ticket.origin.location.coordinates[1])
      setLocationStreet(ticket.origin.street)
      setLocationNumber(ticket.origin.number)
      setLocationState(ticket.origin.state)
      setLocationCity(ticket.origin.city)
      setTag(ticket.origin.tag)
    }
    if (title === 'destino') {
      setLocationLatitude(ticket.destination.location.coordinates[0])
      setLocationLongitude(ticket.destination.location.coordinates[1])
      setLocationStreet(ticket.destination.street)
      setLocationNumber(ticket.destination.number)
      setLocationState(ticket.destination.state)
      setLocationCity(ticket.destination.city)
      setTag(ticket.destination.tag)
    }
  }

  const locationBody = (ticket: Ticket, title: string) => (
    <Button
      type="link"
      style={{
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        textAlign: 'left',
        paddingLeft: '0',
        marginTop: 'min(20px, 40px)',
        marginBottom: 'min(20px, 40px)',
      }}
      onClick={() => {
        handleOpenMapsModal(ticket, title)
      }}
    >
      <span
        style={{
          whiteSpace: 'normal',
        }}
      >
        {title === 'origen' ? ticket.origin.tag : ticket.destination.tag}
      </span>
    </Button>
  )

  const handleOpenEditTicketModal = (ticket: Ticket) => {
    setTicketToEdit(ticket.id)
  }
  const handleOpenAddTicketModal = () => {
    setTicketToEdit(0)
    setOpenModal('add')
  }
  const handleCloseAddTicketModal = () => {
    setOpenModal(undefined)
  }

  useEffect(() => {
    if (!!ticketToEdit) {
      setOpenModal('update')
      setIsTicketModalOpen(true)
    }
  }, [ticketToEdit])

  return (
    <>
      <ContentContainer>
        <StyledRow className="sticky-options border-botom">
          <Tooltip placement="bottomRight" title={t('suppliers_router.new_ticket_modal.tooltip')}>
            <StyledButton
              id="addRequestSupplier"
              className="inline"
              onClick={handleOpenAddTicketModal}
            >
              <PlusOutlined className="option-button" />
            </StyledButton>
          </Tooltip>
          <div
            className={`filters-container${
              filtersDisplayed && !menuDisplayed ? ' filters-displayed' : ''
            }`}
          >
            <Button onClick={handleDisplayFilters} id="displayFiltersButton">
              <FilterOutlined />
            </Button>
            <div className="filters-list">
              <StyledButton className="inline option" onClick={handleCleanFilters}>
                {t('globals.generics.clean_filters')}
              </StyledButton>
              <StyledSelect
                mode="multiple"
                allowClear
                placeholder={t('admin_router.filter_options.status.title')}
                options={filterOptions}
                onChange={handleStatusFilterChange}
                value={filters?.statusName}
                onDropdownVisibleChange={handleCleanSearchInput}
                filterOption={handleFilterOptions}
                maxTagCount="responsive"
              />
              <StyledSelect
                maxTagCount="responsive"
                mode="multiple"
                allowClear
                filterOption={handleFilterOptions}
                placeholder={t('suppliers_router.filter_options.carrier.name')}
                options={allCarriersOptions}
                loading={isLoadingAllCarriers}
                onChange={handleCarrierFilterChange}
                value={filters?.carrierId}
                onDropdownVisibleChange={handleCleanSearchInput}
              />
              <StyledSelect
                maxTagCount="responsive"
                mode="multiple"
                allowClear
                placeholder={t('suppliers_router.filter_options.material.title')}
                options={materialsOptions}
                filterOption={handleFilterOptions}
                onChange={handleMaterialFilterChange}
                value={filters?.materialId}
                onDropdownVisibleChange={handleCleanSearchInput}
              />
              <StyledSelect
                maxTagCount="responsive"
                mode="multiple"
                allowClear
                placeholder={t('suppliers_router.filter_options.equipment.title')}
                options={equipmentOptions}
                filterOption={handleFilterOptions}
                onChange={handleEquipmentFilterChange}
                value={filters?.equipmentId}
                onDropdownVisibleChange={handleCleanSearchInput}
              />
              <StyledSelect
                maxTagCount="responsive"
                allowClear
                placeholder={t('suppliers_router.filter_options.order_by.title')}
                options={orderByOptions}
                filterOption={handleFilterOptions}
                onChange={handleOrderByFilterChange}
                value={filters?.orderBy}
                onDropdownVisibleChange={handleCleanSearchInput}
              />
            </div>
          </div>
          <CollapseExpand handleSetRowData={handleSetRowData} preparedData={preparedData} />
        </StyledRow>
        <StyledRow className="ticket-handler">
          <div style={{ flex: 1 }} />
          {pathname === PATHS.SUPPLIER.PREVIOUS || pathname === PATHS.COMMERCIAL.PREVIOUS ? (
            <DateFromToFilter time={TIME.EXPIRED} />
          ) : (
            <RangeTimeSwitcher />
          )}
          {ticketsIsLoading ? <Spin /> : <div style={{ flex: 1 }} />}
        </StyledRow>
        <StyledRow className="data-table-container">
          <ExpandableTable
            expandedRows={expandedRows}
            handleSetRowData={handleSetRowData}
            data={preparedData}
            columns={[
              {
                title: 'ID',
                dataIndex: 'id',
                key: 'id',
              },
              {
                title: 'Remito',
                key: 'remito',
                render: deliveryNoteTemplate,
              },
              {
                title: 'Origen',
                key: 'origen',
                render: (value, ticket) => locationBody(ticket, 'origen'),
              },
              {
                title: 'Destino',
                key: 'destino',
                render: (value, ticket) => locationBody(ticket, 'destino'),
              },
              {
                title: 'Material',
                dataIndex: ['material', 'nameShort'],
                key: 'material.nameShort',
              },
              {
                title: 'Equipo',
                dataIndex: ['equipment', 'nameShort'],
                key: 'equipment.nameShort',
              },
              {
                title: 'Horario',
                dataIndex: 'withdrawalTime',
                key: 'withdrawalTime',
                sorter: true, // Ant Design usa 'sorter' para columnas ordenables
              },
              {
                title: 'Transportista',
                dataIndex: ['carrier', 'businessName'],
                key: 'carrier.businessName',
                render: (value, ticket) =>
                  ticket.status.name === StatusTicketEnum.CONFIRMED ||
                  ticket.status.name === StatusTicketEnum.TRANSIT ||
                  ticket.status.name === StatusTicketEnum.DELIVERED ||
                  ticket.status.name === StatusTicketEnum.NO_SHOW
                    ? ticket.carrier?.businessName
                    : null,
              },
              {
                title: 'Estado',
                dataIndex: ['status', 'name'],
                key: 'status.name',
                render: (value, { status }) =>
                  t(`globals.generics.status.${status.name.toLowerCase()}`),
              },
              {
                title: 'Tipo',
                dataIndex: 'tripType',
                key: 'tripType',
                render: value => t(`trip_type.${value}`),
              },
              {
                title: 'Acciones',
                key: 'acciones',
                align: 'center',
                render: (_value, ticket) => actionsBody(ticket),
              },
            ]}
          />
        </StyledRow>
      </ContentContainer>
      {/* TODO: New supplier modal to update a request */}
      {openModal && createdTicketStates.includes(openModal) && (
        <UpdateTicketModalSupplier
          title={t('suppliers_router.update_ticket_modal.update')}
          onClose={callback => {
            handleCloseTicketModal()
            if (callback) callback()
          }}
          onOk={submitHandler}
          isLoading={changeTicketLoading}
          open={openModal && createdTicketStates.includes(openModal)}
        />
      )}
      {/* TODO: New supplier modal to create a request */}
      {openModal === 'add' && (
        <AddTicketModalSupplier
          title={t('suppliers_router.new_ticket_modal.title')}
          onClose={callback => {
            handleCloseAddTicketModal()
            if (callback) callback()
          }}
          onOk={submitHandler}
          isLoading={isCreateTicketLoading}
          open={openModal === 'add'}
        />
      )}
      {/* TODO: New supplier modal to create a request */}
      <DeleteTicketModal
        open={isDeleteTicketModalOpen}
        title={t('suppliers_router.delete_ticket_modal.title')}
        onClose={() => setIsDeleteTicketModalOpen(false)}
        ticketId={ticketToDelete}
        isLoading={isDeleteTicketLoading}
      />
      <MapsModal
        open={isMapsModalOpen}
        longitude={locationLongitude}
        latitude={locationLatitude}
        street={locationStreet}
        number={locationNumber}
        province={locationState}
        city={locationCity}
        close={handleCloseMapsModal}
        tag={tag}
      />
    </>
  )
}

export { SuppliersDashboard }
